import { spawn } from "node:child_process";
import { createInterface } from "node:readline/promises";
import type { Readable, Writable } from "node:stream";

import { CliError } from "../errors/index.js";

import type {
  ExternalInvocation,
  ExternalSetupRunner,
  PackageManager,
  SetupIo,
} from "./types.js";

export function resolveExternalRunner(env: NodeJS.ProcessEnv): ExternalSetupRunner {
  const userAgent = env.npm_config_user_agent?.toLowerCase() ?? "";
  const execPath = env.npm_execpath?.toLowerCase() ?? "";

  if (userAgent.includes("bun/") || execPath.includes("bun")) {
    return "bunx";
  }
  if (userAgent.includes("pnpm/") || execPath.includes("pnpm")) {
    return "pnpx";
  }
  return "npx";
}

export function resolveSkillsRunner(env: NodeJS.ProcessEnv): ExternalSetupRunner {
  const runner = resolveExternalRunner(env);
  return runner === "bunx" ? "npx" : runner;
}

export function resolvePackageManager(env: NodeJS.ProcessEnv): PackageManager {
  const runner = resolveExternalRunner(env);
  if (runner === "bunx") {
    return "bun";
  }
  if (runner === "pnpx") {
    return "pnpm";
  }
  return "npm";
}

export function buildGlobalPackageInvocation(packageManager: PackageManager, packageSpec: string) {
  if (packageManager === "bun") {
    return { args: ["add", "-g", "--silent", packageSpec], command: "bun" };
  }
  if (packageManager === "pnpm") {
    return { args: ["add", "-g", "--silent", packageSpec], command: "pnpm" };
  }
  return { args: ["install", "-g", "--silent", packageSpec], command: "npm" };
}

export async function runPassthroughInvocation(invocation: ExternalInvocation, io: SetupIo) {
  const code = await runInvocation(invocation, io, "passthrough", {
    forwardStdin: false,
  });
  if (code !== 0) {
    throw new CliError(
      "INSTALL_FAILED",
      `${invocation.command} exited with code ${String(code)}`,
    );
  }
}

export async function runQuietInvocation(invocation: ExternalInvocation, io: SetupIo) {
  const result = await runInvocation(invocation, io, "quiet", {
    forwardStdin: false,
  });
  if (typeof result === "number") {
    throw new CliError("INSTALL_FAILED", "Quiet runner returned an unexpected exit shape");
  }
  const { code, stderr, stdout } = result;
  if (code !== 0) {
    if (stdout.length > 0) {
      await writeTo(io.stdout, stdout);
    }
    if (stderr.length > 0) {
      await writeTo(io.stderr, stderr);
    }
    throw new CliError(
      "INSTALL_FAILED",
      `${invocation.command} exited with code ${String(code)}`,
    );
  }
}

export function isInteractiveTerminal(stream: Readable | Writable) {
  const maybeTty = stream as unknown as { isTTY?: unknown };
  return typeof maybeTty.isTTY === "boolean" && maybeTty.isTTY;
}

export async function promptYesNo(io: SetupIo, message: string) {
  const reader = createInterface({
    input: io.stdin,
    output: io.stdout,
  });

  try {
    const answer = await reader.question(`${message} [y/N] `);
    const normalized = answer.trim().toLowerCase();
    return normalized === "y" || normalized === "yes";
  } finally {
    reader.close();
  }
}

export async function runInteractiveInvocation(invocation: ExternalInvocation, io: SetupIo) {
  if (shouldInheritInteractiveStdio(io)) {
    const code = await runInheritedInvocation(invocation, io);
    if (code !== 0) {
      throw new CliError(
        "INSTALL_FAILED",
        `${invocation.command} exited with code ${String(code)}`,
      );
    }
    return;
  }

  const code = await runInvocation(invocation, io, "passthrough", {
    forwardStdin: true,
  });
  if (code !== 0) {
    throw new CliError(
      "INSTALL_FAILED",
      `${invocation.command} exited with code ${String(code)}`,
    );
  }
}

export function shouldInheritInteractiveStdio(io: SetupIo) {
  return (
    io.stdin === process.stdin &&
    io.stdout === process.stdout &&
    io.stderr === process.stderr &&
    isInteractiveTerminal(io.stdin) &&
    isInteractiveTerminal(io.stdout) &&
    isInteractiveTerminal(io.stderr)
  );
}

async function runInvocation(
  invocation: ExternalInvocation,
  io: SetupIo,
  mode: "passthrough" | "quiet",
  options: { forwardStdin: boolean },
) {
  return await new Promise<
    | number
    | {
        code: number;
        stderr: string;
        stdout: string;
      }
  >((resolve, reject) => {
    const child = spawn(invocation.command, invocation.args, {
      cwd: io.cwd,
      env: io.env,
      stdio: ["pipe", "pipe", "pipe"],
    });

    const stdoutChunks: Buffer[] = [];
    const stderrChunks: Buffer[] = [];

    child.on("error", (error) => {
      reject(
        new CliError(
          "INSTALL_FAILED",
          `Failed to start ${invocation.command}: ${error.message}`,
        ),
      );
    });

    child.on("close", (code, signal) => {
      if (options.forwardStdin) {
        io.stdin.unpipe(child.stdin);
      }
      if (options.forwardStdin && typeof io.stdin.pause === "function") {
        io.stdin.pause();
      }

      if (signal !== null) {
        reject(
          new CliError("INSTALL_FAILED", `${invocation.command} exited with signal ${signal}`),
        );
        return;
      }

      const exitCode = code ?? 1;
      if (mode === "passthrough") {
        resolve(exitCode);
        return;
      }

      resolve({
        code: exitCode,
        stderr: Buffer.concat(stderrChunks).toString("utf8"),
        stdout: Buffer.concat(stdoutChunks).toString("utf8"),
      });
    });

    if (mode === "passthrough") {
      if (options.forwardStdin) {
        io.stdin.pipe(child.stdin);
      } else {
        child.stdin.end();
      }
      child.stdout.on("data", (chunk) => {
        io.stdout.write(chunk);
      });
      child.stderr.on("data", (chunk) => {
        io.stderr.write(chunk);
      });
      return;
    }

    child.stdin.end();
    child.stdout.on("data", (chunk) => {
      stdoutChunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(String(chunk)));
    });
    child.stderr.on("data", (chunk) => {
      stderrChunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(String(chunk)));
    });
  });
}

async function runInheritedInvocation(invocation: ExternalInvocation, io: SetupIo) {
  return await new Promise<number>((resolve, reject) => {
    const child = spawn(invocation.command, invocation.args, {
      cwd: io.cwd,
      env: io.env,
      stdio: "inherit",
    });

    child.on("error", (error) => {
      reject(
        new CliError(
          "INSTALL_FAILED",
          `Failed to start ${invocation.command}: ${error.message}`,
        ),
      );
    });

    child.on("close", (code, signal) => {
      if (signal !== null) {
        reject(
          new CliError("INSTALL_FAILED", `${invocation.command} exited with signal ${signal}`),
        );
        return;
      }

      resolve(code ?? 1);
    });
  });
}

async function writeTo(stream: Writable, text: string) {
  await new Promise<void>((resolve, reject) => {
    stream.write(text, (error) => {
      if (error == null) {
        resolve();
        return;
      }
      reject(error);
    });
  });
}
